import { BBox, bbox as getBoundingBox, Geometry } from '@turf/turf'
import { useRuntimeVariables } from 'app-kit'
import { isNil } from 'lodash'
import { forwardRef, ReactNode, useMemo } from 'react'
import { Layer, LngLatBoundsLike, Map, MapRef, Source } from 'react-map-gl'
import { mapStyle } from 'styles/mapBox'
import { GeckoSite } from 'types'

type AllValuesUndefined<T> = {
  [K in keyof T]: undefined
}

type SiteGeometry = {
  geometry: Geometry
  bbox: LngLatBoundsLike
}

interface SiteFocusedMapProps {
  site: GeckoSite
  children?: ReactNode
}

export const SiteFocusedMap = forwardRef<MapRef, SiteFocusedMapProps>(
  ({ children = null, ...props }, ref) => {
    const { MAPBOX_KEY } = useRuntimeVariables()

    const { geometry: siteGeometry, bbox } = useMemo(():
      | SiteGeometry
      | AllValuesUndefined<SiteGeometry> => {
      const geometry = props.site.data.geography
      if (isNil(geometry)) {
        return {
          geometry: undefined,
          bbox: undefined,
        }
      }
      const bounds = geometry.bbox ?? getBoundingBox(geometry)

      return {
        geometry,
        bbox: as2dBBox(bounds),
      }
    }, [props.site.data.geography])

    return (
      <Map
        ref={ref}
        interactive={true}
        scrollZoom={true}
        touchZoomRotate={true}
        mapboxAccessToken={MAPBOX_KEY}
        initialViewState={{
          bounds: bbox,
          fitBoundsOptions: {
            padding: 20,
          },
        }}
        style={{
          display: 'flex',
          flex: 1,
          borderRadius: '6px',
          transition: 'opacity 300ms',
          opacity: 1,
        }}
        mapStyle={mapStyle}>
        <Source type={'geojson'} data={siteGeometry}>
          <Layer
            id={'polygonOutline'}
            type={'line'}
            paint={{
              'line-color': 'rgba(0,0,0,1)',
              'line-width': 5,
              'line-dasharray': [2, 2],
            }}
          />
        </Source>
        {children}
      </Map>
    )
  }
)
SiteFocusedMap.displayName = 'SiteFocusedMap' // needed for forwardRef to work properly

function as2dBBox(bbox: BBox): LngLatBoundsLike {
  if (is2dBBox(bbox)) {
    return bbox
  }
  // is 3d bbox, convert to 2d
  return [bbox[0], bbox[1], bbox[3], bbox[4]]
}

function is2dBBox(bbox: BBox): bbox is [number, number, number, number] {
  return bbox.length === 4
}
